import { ComparisonModeStrings } from 'enums/ComparisonMode'
import CrosstabDisplayType from 'enums/CrosstabDisplayType'
import CrosstabDisplayTypeDetail from 'enums/CrosstabDisplayTypeDetail'
import CrosstabSortOption from 'enums/CrosstabSortOption'
import i18n from 'i18n'
import { CrosstabWidgetsType, BarColumnData, PivotWithCellMarker } from '../../types'
import {
  arrangeResult,
  genSeriesData,
  sortByMetric,
  getCategorizedSeries
} from '../utils/displayDataUtils'

interface Point {
  x: number
  y: number
}

export const getTotalType = (widgetSetting: CrosstabWidgetsType) => {
  const totalType = widgetSetting.ctDisplayTotal
  const percentShareType = widgetSetting.ctPercentShare
  const displayType = widgetSetting.ctDisplay

  // fall back to percent share type to determine row base or column base
  if (displayType === CrosstabDisplayType.VALUE && totalType === CrosstabDisplayTypeDetail.NONE) {
    return CrosstabDisplayTypeDetail.ROWS
  }
  if (percentShareType === CrosstabDisplayTypeDetail.ROW_GRAND_TOTAL) {
    return CrosstabDisplayTypeDetail.COLUMNS
  }
  if (totalType === CrosstabDisplayTypeDetail.NONE) {
    return percentShareType !== CrosstabDisplayTypeDetail.COLUMNS
      ? CrosstabDisplayTypeDetail.ROWS
      : CrosstabDisplayTypeDetail.COLUMNS
  }

  return totalType
}

export const tooltipValueFormatter = (point: Point) => {
  if (point.y > 0 && point.y < 1) {
    return `<1%`
  }
  return `${Math.round(point.y)}%`
}

export const getBarColumnSetting = (
  result: PivotWithCellMarker[],
  widgetSetting: CrosstabWidgetsType,
  rowIds: string[],
  rowNames: string[],
  colIds: string[],
  colNames: string[],
  rowComparisonMode: ComparisonModeStrings,
  colComparisonMode: ComparisonModeStrings
) => {
  const barRowIds = [...rowIds].reverse()
  const barRowNames = [...rowNames].reverse()

  const barColIds = [...colIds]
  const barColNames = [...colNames]

  const totalType = getTotalType(widgetSetting)
  const percentShareType = widgetSetting.ctPercentShare

  let legendNames
  switch (totalType) {
    case CrosstabDisplayTypeDetail.COLUMNS:
      legendNames = barColNames
      break
    default:
    case CrosstabDisplayTypeDetail.ROWS:
      legendNames = barRowNames
      break
  }

  // generate two dimensional array as coordinates based on the order of rowIds & colIds.
  const arrangedResult = arrangeResult(
    result,
    barRowIds,
    barColIds,
    rowComparisonMode,
    colComparisonMode
  )

  // gen series data
  const seriesData = genSeriesData(arrangedResult, widgetSetting, true)

  if (
    widgetSetting.ctSort === CrosstabSortOption.ROW_METRIC_ASC ||
    widgetSetting.ctSort === CrosstabSortOption.ROW_METRIC_DES ||
    widgetSetting.ctSort === CrosstabSortOption.COLUMN_METRIC_ASC ||
    widgetSetting.ctSort === CrosstabSortOption.COLUMN_METRIC_DES
  ) {
    sortByMetric(seriesData, widgetSetting, barRowIds, barRowNames, barColIds, barColNames)
  }

  // When displaying total for ROWS/NONE (of a column), we group by columns
  // The seriesData is formed in [[layers]],
  // therefore, seriesData needs to be
  // [row1([col1,col2,col3]),row2([col1,col2,col3])]
  // meaning we want to group by rows, instead of a column
  const reversedType =
    totalType !== CrosstabDisplayTypeDetail.COLUMNS
      ? CrosstabDisplayTypeDetail.COLUMNS
      : CrosstabDisplayTypeDetail.ROWS
  const categorizedSeries = getCategorizedSeries(seriesData, reversedType)
  const showPercentShareOfRowGrandTotal =
    percentShareType === CrosstabDisplayTypeDetail.ROW_GRAND_TOTAL

  const layers: BarColumnData[] = []
  for (let i = 0; i < categorizedSeries.length; i += 1) {
    const layer: BarColumnData = {} as BarColumnData
    layer.data = []

    for (let j = 0; j < categorizedSeries[i].length; j += 1) {
      const cell = categorizedSeries[i][j]
      const { x, y, isRowGrandTotal } = cell

      if (!isRowGrandTotal) {
        let { value } = cell
        if (showPercentShareOfRowGrandTotal) {
          const granTotalOfRow = categorizedSeries[categorizedSeries.length - 1][j]
          value = (cell.value / granTotalOfRow.value) * 100
        }

        layer.data.push({
          y: value,
          custom: {
            rowId: barRowIds[y],
            colId: barColIds[x]
          }
        })
      }
    }
    layer.name = legendNames[i]

    // Ning's TODO: logic better use key to determine instaed of display name
    if (!(layer.name === i18n.t('topicTotal') || layer.name === i18n.t('audienceTotal'))) {
      layers.push(layer)
    }
  }
  return { barSeriesData: layers, barRowNames, barColNames }
}

export const getXAxisNames = (
  widgetSetting: CrosstabWidgetsType,
  rowNames: string[],
  columnNames: string[]
) => {
  if (widgetSetting.ctDisplay === CrosstabDisplayType.PERCENT_SHARE) {
    if (widgetSetting.ctPercentShare === CrosstabDisplayTypeDetail.ROW_GRAND_TOTAL) {
      return rowNames
    }
    if (widgetSetting.ctDisplayTotal === CrosstabDisplayTypeDetail.NONE) {
      return widgetSetting.ctPercentShare === CrosstabDisplayTypeDetail.COLUMNS
        ? rowNames
        : columnNames
    }
    if (widgetSetting.ctDisplayTotal === CrosstabDisplayTypeDetail.COLUMNS) {
      return rowNames
    }
    return columnNames
  }
  if (widgetSetting.ctDisplayTotal === CrosstabDisplayTypeDetail.COLUMNS) {
    return rowNames
  }
  return columnNames
}
